import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  IconButton, // ✅ Added
} from "@mui/material";
import { Delete as DeleteIcon, InsertDriveFile as FileIcon, CheckCircle as SuccessIcon, CloudUpload as UploadIcon } from "@mui/icons-material"; // ✅ Updated

const StudentSubmissionStepContent = ({
  activeStep,
  formData,
  handleChange,
  handleFileUpload,
  handleDeleteFile, // ✅ Added
  fileInputRef,
  handleSubmit,
  isArabic,
  t,
  isEdit = false,
}) => {
  const renderFileUpload = (field, label, inputId) => {
    const file = formData[field];
    const isFile = file instanceof File;
    const isExistingPath = typeof file === "string" && file.length > 0;

    return (
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{ mb: 1, textAlign: isArabic ? "right" : "left" }}
        >
          {label}
        </Typography>
        <Paper
          elevation={0}
          onClick={() => !isFile && document.getElementById(inputId)?.click()}
          sx={{
            border: (isFile || isExistingPath) ? "2px solid #4CAF50" : "2px dashed #C5D1D8",
            borderRadius: 2,
            minHeight: 120,
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: (isFile || isExistingPath) ? "default" : "pointer",
            bgcolor: (isFile || isExistingPath) ? "rgba(76, 175, 80, 0.02)" : "transparent",
            transition: "all 0.2s ease",
            '&:hover': {
              backgroundColor: (isFile || isExistingPath) ? "rgba(76, 175, 80, 0.05)" : "rgba(98, 143, 255, 0.04)",
              borderColor: (isFile || isExistingPath) ? "#4CAF50" : "#628FFF",
            },
          }}
        >
          {isFile || isExistingPath ? (
            <Box sx={{ width: "100%", minWidth: 0, display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, justifyContent: "space-between", gap: { xs: 1.5, sm: 1 }, px: { xs: 0, sm: 1 } }}>
              <Box sx={{ minWidth: 0, display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}>
                <FileIcon sx={{ color: "#4CAF50", fontSize: 32 }} />
                <Box sx={{ minWidth: 0, flex: 1, textAlign: isArabic ? "right" : "left" }}>
                  <Typography 
                    variant="body2" 
                    fontWeight={600} 
                    sx={{ 
                      maxWidth: "100%",
                      overflow: "hidden", 
                      textOverflow: "ellipsis", 
                      whiteSpace: "nowrap",
                      cursor: isExistingPath ? "pointer" : "default",
                      color: isExistingPath ? "primary.main" : "text.primary",
                      '&:hover': {
                        textDecoration: isExistingPath ? "underline" : "none"
                      }
                    }}
                    onClick={() => {
                      if (isExistingPath) {
                        window.open(`${process.env.REACT_APP_API_URL}/api/student-admissions/download/${file}`, "_blank");
                      }
                    }}
                  >
                    {isFile ? file.name : t("studentDashboard.submissions.edit.currentDocument")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isFile ? `${(file.size / (1024 * 1024)).toFixed(2)} MB` : t("studentDashboard.submissions.edit.alreadyUploaded")}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: { xs: "flex-end", sm: "initial" }, gap: 1, flexShrink: 0 }}>
                <SuccessIcon sx={{ color: "#4CAF50" }} />
                <Button 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    document.getElementById(inputId)?.click();
                  }}
                  sx={{ textTransform: "none", fontSize: "0.75rem" }}
                >
                  {t("studentDashboard.submissions.edit.change")}
                </Button>
                <IconButton 
                  color="error" 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteFile(field);
                  }}
                  sx={{ bgcolor: "rgba(211, 47, 47, 0.05)", '&:hover': { bgcolor: "rgba(211, 47, 47, 0.1)" } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: "center", py: 1 }}>
              <UploadIcon sx={{ fontSize: 44, mb: 1.5, color: "#628FFF", opacity: 0.8 }} />
              <Typography color="text.primary" variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                {t("studentDashboard.submissions.placeholders.dragFiles", { defaultValue: isArabic ? "اختر ملفاً" : "Choose a file" })}
              </Typography>
              <Typography color="text.secondary" variant="caption" sx={{ display: 'block' }}>
                {t("studentDashboard.submissions.edit.dragAndDrop")}
              </Typography>
            </Box>
          )}
          <input 
            id={inputId} 
            type="file" 
            hidden 
            onChange={handleFileUpload(field)} 
            accept=".pdf,.png,.jpg,.jpeg"
          />
        </Paper>
      </Box>
    );
  };

  const renderAllFields = () => {
    // Collect all elements from all steps
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, width: "100%", minWidth: 0, boxSizing: "border-box" }}>
        <Grid container spacing={3}>
          {/* Section 1: Personal */}
          <Grid item xs={12}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1, color: "primary.main" }}>
              {t("studentDashboard.submissions.edit.personalInformation")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.firstName")} value={formData.firstName} onChange={handleChange("firstName")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.lastName")} value={formData.lastName} onChange={handleChange("lastName")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.email")} value={formData.email} onChange={handleChange("email")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.phone")} value={formData.phone} onChange={handleChange("phone")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.dob")} value={formData.dob} onChange={handleChange("dob")} type="date" InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.nationality")} value={formData.nationality} onChange={handleChange("nationality")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.passportNumber")} value={formData.passportNumber} onChange={handleChange("passportNumber")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.city")} value={formData.city} onChange={handleChange("city")} />
          </Grid>

          {/* Section 2: Academic */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1, color: "primary.main" }}>
              {t("studentDashboard.submissions.edit.academicInformation")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.institutionName")} value={formData.institutionName} onChange={handleChange("institutionName")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.gpa")} value={formData.gpa} onChange={handleChange("gpa")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.desiredProgram")} value={formData.desiredProgram} onChange={handleChange("desiredProgram")} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={t("studentDashboard.submissions.fields.programLevel")} value={formData.programLevel} onChange={handleChange("programLevel")} />
          </Grid>

          {/* Section 3: Documents */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Typography variant="h6" fontWeight={800} sx={{ mb: 1, color: "primary.main" }}>
              {t("studentDashboard.submissions.edit.documents")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderFileUpload("certifications", t("studentDashboard.submissions.fields.certifications"), "edit-cert-upload")}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderFileUpload("highSchoolDocument", t("studentDashboard.submissions.fields.highSchoolDocument"), "edit-hs-upload")}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderFileUpload("passportDocument", t("studentDashboard.submissions.fields.passportDocument"), "edit-pass-upload")}
          </Grid>
          <Grid item xs={12} sm={6}>
            {renderFileUpload("transcriptDocument", t("studentDashboard.submissions.fields.transcriptDocument"), "edit-trans-upload")}
          </Grid>
          <Grid item xs={12}>
            {renderFileUpload("motivationLetter", t("studentDashboard.submissions.fields.motivationLetter"), "edit-mot-upload")}
          </Grid>
        </Grid>
      </Box>
    );
  };

  if (isEdit || activeStep === -1) {
    return renderAllFields();
  }

  switch (activeStep) {
    case 0:
      return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ mb: 2, textAlign: isArabic ? "right" : "left" }}
              >
                {t("studentDashboard.submissions.stepTitles.personalInfo")}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.firstName")}
                value={formData.firstName}
                onChange={handleChange("firstName")}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.lastName")}
                value={formData.lastName}
                onChange={handleChange("lastName")}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.email")}
                value={formData.email}
                onChange={handleChange("email")}
                type="email"
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.phone")}
                value={formData.phone}
                onChange={handleChange("phone")}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.dob")}
                value={formData.dob}
                onChange={handleChange("dob")}
                type="date"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.nationality")}
                value={formData.nationality}
                onChange={handleChange("nationality")}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.passportNumber")}
                value={formData.passportNumber}
                onChange={handleChange("passportNumber")}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.city")}
                value={formData.city}
                onChange={handleChange("city")}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      );
    case 1:
      const qualificationOptions = t("studentDashboard.submissions.dropdownOptions.previousQualification", {
        returnObjects: true,
      });
      const graduationYears = Array.from({ length: 20 }, (_, i) => {
        const year = new Date().getFullYear() - i;
        return year.toString();
      }).reverse();
      const programOptions = t("studentDashboard.submissions.dropdownOptions.desiredProgram", {
        returnObjects: true,
      });
      const levelOptions = t("studentDashboard.submissions.dropdownOptions.programLevel", {
        returnObjects: true,
      });
      const semesterOptions = t("studentDashboard.submissions.dropdownOptions.semesterStart", {
        returnObjects: true,
      });

      return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ textAlign: isArabic ? "right" : "left" }}>
                  {t("studentDashboard.submissions.fields.previousQualification")}
                </InputLabel>
                <Select
                  value={formData.previousQualification}
                  label={t("studentDashboard.submissions.fields.previousQualification")}
                  onChange={handleChange("previousQualification")}
                  sx={{ direction: isArabic ? "rtl" : "ltr" }}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        direction: isArabic ? "rtl" : "ltr",
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t("studentDashboard.submissions.placeholders.select")}</em>
                  </MenuItem>
                  {qualificationOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.institutionName")}
                placeholder={t("studentDashboard.submissions.placeholders.institutionName")}
                value={formData.institutionName}
                onChange={handleChange("institutionName")}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
                inputProps={{
                  dir: isArabic ? "rtl" : "ltr",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ textAlign: isArabic ? "right" : "left" }}>
                  {t("studentDashboard.submissions.fields.graduationYear")}
                </InputLabel>
                <Select
                  value={formData.graduationYear}
                  label={t("studentDashboard.submissions.fields.graduationYear")}
                  onChange={handleChange("graduationYear")}
                  sx={{ direction: isArabic ? "rtl" : "ltr" }}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        direction: isArabic ? "rtl" : "ltr",
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t("studentDashboard.submissions.placeholders.select")}</em>
                  </MenuItem>
                  {graduationYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.gpa")}
                value={formData.gpa}
                onChange={handleChange("gpa")}
                placeholder="e.g. 3.5"
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ textAlign: isArabic ? "right" : "left" }}>
                  {t("studentDashboard.submissions.fields.desiredProgram")}
                </InputLabel>
                <Select
                  value={formData.desiredProgram}
                  label={t("studentDashboard.submissions.fields.desiredProgram")}
                  onChange={handleChange("desiredProgram")}
                  sx={{ direction: isArabic ? "rtl" : "ltr" }}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        direction: isArabic ? "rtl" : "ltr",
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t("studentDashboard.submissions.placeholders.select")}</em>
                  </MenuItem>
                  {programOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ textAlign: isArabic ? "right" : "left" }}>
                  {t("studentDashboard.submissions.fields.programLevel")}
                </InputLabel>
                <Select
                  value={formData.programLevel}
                  label={t("studentDashboard.submissions.fields.programLevel")}
                  onChange={handleChange("programLevel")}
                  sx={{ direction: isArabic ? "rtl" : "ltr" }}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        direction: isArabic ? "rtl" : "ltr",
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t("studentDashboard.submissions.placeholders.select")}</em>
                  </MenuItem>
                  {levelOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ textAlign: isArabic ? "right" : "left" }}>
                  {t("studentDashboard.submissions.fields.semesterStart")}
                </InputLabel>
                <Select
                  value={formData.semesterStart}
                  label={t("studentDashboard.submissions.fields.semesterStart")}
                  onChange={handleChange("semesterStart")}
                  sx={{ direction: isArabic ? "rtl" : "ltr" }}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        direction: isArabic ? "rtl" : "ltr",
                      },
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>{t("studentDashboard.submissions.placeholders.select")}</em>
                  </MenuItem>
                  {semesterOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      );
    case 2:
      return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ textAlign: isArabic ? "right" : "left" }}>
                  {t("studentDashboard.submissions.fields.englishLevel")}
                </InputLabel>
                <Select
                  value={formData.englishLevel}
                  label={t("studentDashboard.submissions.fields.englishLevel")}
                  onChange={handleChange("englishLevel")}
                  sx={{ direction: isArabic ? "rtl" : "ltr" }}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        direction: isArabic ? "rtl" : "ltr",
                      },
                    },
                  }}
                >
                  <MenuItem value="A1">A1</MenuItem>
                  <MenuItem value="A2">A2</MenuItem>
                  <MenuItem value="B1">B1</MenuItem>
                  <MenuItem value="B2">B2</MenuItem>
                  <MenuItem value="C1">C1</MenuItem>
                  <MenuItem value="C2">C2</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ textAlign: isArabic ? "right" : "left" }}>
                  {t("studentDashboard.submissions.fields.germanLevel")}
                </InputLabel>
                <Select
                  value={formData.germanLevel}
                  label={t("studentDashboard.submissions.fields.germanLevel")}
                  onChange={handleChange("germanLevel")}
                  sx={{ direction: isArabic ? "rtl" : "ltr" }}
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      sx: {
                        direction: isArabic ? "rtl" : "ltr",
                      },
                    },
                  }}
                >
                  <MenuItem value="A1">A1</MenuItem>
                  <MenuItem value="A2">A2</MenuItem>
                  <MenuItem value="B1">B1</MenuItem>
                  <MenuItem value="B2">B2</MenuItem>
                  <MenuItem value="C1">C1</MenuItem>
                  <MenuItem value="C2">C2</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t("studentDashboard.submissions.fields.otherLanguages")}
                placeholder={t("studentDashboard.submissions.placeholders.otherLanguages")}
                value={formData.otherLanguages}
                onChange={handleChange("otherLanguages")}
                InputProps={{
                  sx: {
                    direction: isArabic ? "rtl" : "ltr",
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
                inputProps={{
                  dir: isArabic ? "rtl" : "ltr",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              {renderFileUpload("certifications", t("studentDashboard.submissions.fields.certifications"), "certifications-upload")}
            </Grid>
          </Grid>
        </Box>
      );
    case 3:
      return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              {renderFileUpload("highSchoolDocument", t("studentDashboard.submissions.fields.highSchoolDocument"), "highSchool-upload")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderFileUpload("passportDocument", t("studentDashboard.submissions.fields.passportDocument"), "passport-upload")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderFileUpload("transcriptDocument", t("studentDashboard.submissions.fields.transcriptDocument"), "transcript-upload")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderFileUpload("motivationLetter", t("studentDashboard.submissions.fields.motivationLetter"), "motivation-upload")}
            </Grid>
          </Grid>
        </Box>
      );
    case 4:
      return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 3, textAlign: isArabic ? "right" : "left" }}
          >
            {t("studentDashboard.submissions.stepTitles.review")}
          </Typography>
          <Grid container spacing={3}>
            {Object.entries(formData).filter(([key]) => key !== "audioMessage").map(([key, value]) => (
              <Grid item xs={12} sm={6} key={key}>
                <Paper sx={{ p: 2, borderRadius: 3, bgcolor: "#F7F9FA" }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    {t(`studentDashboard.submissions.review.${key}`, { defaultValue: key })}
                  </Typography>
                    {value instanceof File ? value.name : (value || t("studentDashboard.submissions.notProvided"))}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      );
    default:
      return null;
  }
};

export default StudentSubmissionStepContent;
